import { useMutation } from "@apollo/client/react";
import {
  ContentStatus,
  SimulationCaseById,
  UpdateSimulationCase,
  UpdateSimulationCaseVariables,
} from "__generated__/types";
import leftArrowLogo from "common/assets/arrow_left.svg";
import rightArrowLogo from "common/assets/arrow_right.svg";
import { Button, CancelOrSubmit } from "common/buttons";
import { Field } from "common/components/forms/Field";
import { Label } from "common/components/forms/Label";
import { MarkdownField } from "common/components/forms/MarkdownField";
import { MutationLoadingOrError } from "common/components/forms/MutationLoadingOrError";
import { TextField } from "common/components/forms/TextField";
import { AdministrationLayout } from "common/components/layouts/AdministrationLayout";
import { LoadingLayout } from "common/components/layouts/LoadingLayout";
import { NoDataError } from "common/components/NoDataError";
import { Select } from "common/components/Select";
import { contentStatusOptions } from "common/content-status";
import { useSimpleQuery } from "common/graphql/useSimpleQuery";
import { markdownInput } from "common/input-types";
import { urls } from "common/urls";
import { MUTATION, QUERY } from "domain/common/simulation-mutations";
import {
  clinicalCategories,
  difficulties,
  modules,
} from "domain/simulation/utils";
import React, { useEffect, useRef, useState } from "react";
import { FormProvider, useForm, UseFormReturn } from "react-hook-form";
import { useNavigate, useParams } from "react-router";
import ReactSelect, { SingleValue } from "react-select";
import { CreateContentFooterBox } from "../common/CreateContentFooterBox";
import { removeTypename } from "../util";
import { AddNextCase } from "./AddNextCase";
import styles from "./SimulationAdmin.module.css";

interface FormValues {
  title: string;
  instantReferral: string;
  patientInfo: string;
  assessments: Assessment[];
  status: string;
  nextCase: string;
}

interface Assessment {
  testName: string;
  testResult: { content: string; isMarkdown: boolean };
}

type OptionType = {
  value: string;
  label: string;
};

type RouteProps = {
  simulationCaseId: string;
};

function assessmentValues(
  assessments: Assessment[],
  methods: UseFormReturn<FormValues>,
): Assessment[] {
  return assessments.map(
    (_assessment: Assessment, index: number): Assessment => ({
      testName: methods.getValues(`assessments.${index}.testName`),
      testResult: {
        content: methods.getValues(`assessments.${index}.testResult.content`),
        isMarkdown: true,
      },
    }),
  );
}

function filterOptions(
  items: OptionType[],
  selectedValues: string | undefined | null,
): OptionType {
  return items.find(({ value }) => selectedValues?.includes(value));
}

function extractIdFromNextCase(nextCase: string): string {
  return nextCase.split(" ").at(-1)?.slice(1, -1) ?? nextCase;
}

export const SimulationEditPage: React.FC = () => {
  const methods = useForm<FormValues>();
  const { setValue } = methods;

  const navigate = useNavigate();
  const { simulationCaseId } = useParams<RouteProps>();

  const [contentStatus, setContentStatus] = useState<
    ContentStatus | undefined
  >();

  const [moduleSelected, setModuleSelected] =
    useState<SingleValue<OptionType> | null>();
  const [difficultySelected, setDifficultySelected] =
    useState<SingleValue<OptionType> | null>();
  const [categorySelected, setCategorySelected] =
    useState<SingleValue<OptionType> | null>();

  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [currentAssessmentIndex, setCurrentAssessmentIndex] = useState(0);

  const [instantRefValue, setInstantRefValue] = useState<boolean>(true);

  const [nextCase, setNextCase] = useState<string>("");

  const bottomRef = useRef<null | HTMLDivElement>(null);

  const { data, loadingOrError, refetch } = useSimpleQuery<SimulationCaseById>(
    QUERY,
    {
      variables: { id: simulationCaseId },
      fetchPolicy: "network-only",
    },
  );

  const [mutate, { error, loading }] = useMutation<
    UpdateSimulationCase,
    UpdateSimulationCaseVariables
  >(MUTATION);

  useEffect(() => {
    if (data && data.simulationCaseById) {
      const {
        status,
        module,
        difficulty,
        clinicalCategory,
        assessments,
        instantReferral,
        nextCase,
      } = data.simulationCaseById;

      setContentStatus(status ?? ContentStatus.DRAFT);
      setModuleSelected(filterOptions(modules, module));
      setDifficultySelected(filterOptions(difficulties, difficulty));
      setCategorySelected(filterOptions(clinicalCategories, clinicalCategory));
      setCurrentAssessmentIndex(0);
      setInstantRefValue(instantReferral?.referralValue ?? true);
      setAssessments(
        assessments?.map((assessment) => ({
          testName: assessment?.testName ?? "",
          testResult: {
            content: assessment?.testResult?.content ?? "",
            isMarkdown: assessment?.testResult?.isMarkdown ?? true,
          },
        })) ?? [],
      );
      setNextCase(typeof nextCase?.title === "string" ? nextCase.title : "");
    }
  }, [data]);

  useEffect(() => {
    if (
      assessments.length === 0 ||
      currentAssessmentIndex >= assessments.length
    )
      return;

    const currentAssessment = assessments[currentAssessmentIndex];
    setValue("assessments", assessments);
    setValue(
      `assessments.${currentAssessmentIndex}.testName`,
      currentAssessment.testName || "",
    );
    setValue(
      `assessments.${currentAssessmentIndex}.testResult.content`,
      currentAssessment.testResult.content || "",
    );
  }, [currentAssessmentIndex, assessments, setValue]);

  const changeAssessment = (newIndex: number) => {
    if (newIndex < 0 || newIndex >= assessments.length) return;
    setAssessments(assessmentValues(assessments, methods));
    setCurrentAssessmentIndex(newIndex);
    scrollToBottom();
  };

  const addAssessment = () => {
    setAssessments(assessmentValues(assessments, methods));
    setAssessments((prev) => [
      ...prev,
      { testName: "", testResult: { content: "", isMarkdown: true } },
    ]);
    setCurrentAssessmentIndex(assessments.length);
  };

  const removeAssessment = (index: number) => {
    if (index < assessments.length) {
      setAssessments((prev) => prev.filter((_, i) => i !== index));
      setCurrentAssessmentIndex((prev) => (prev > 0 ? prev - 1 : 0));
    }
  };

  const scrollToBottom = () => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  if (!simulationCaseId) {
    throw new Error("Unexpected error: Missing simulationCaseId");
  }

  if (loadingOrError) {
    return <LoadingLayout>{loadingOrError}</LoadingLayout>;
  }

  if (!data || !data.simulationCaseById) {
    return (
      <LoadingLayout>
        <NoDataError />
      </LoadingLayout>
    );
  }

  const {
    title,
    patientInformation,
    diagnosis,
    treatment,
    referral,
    instantReferral,
  } = data.simulationCaseById;

  const onSubmit = async (input: FormValues) => {
    if (
      !contentStatus ||
      !moduleSelected ||
      !difficultySelected ||
      !categorySelected
    ) {
      alert("Please ensure all required fields are selected.");
      return;
    }

    const cleanedAssessments = assessmentValues(assessments, methods).map(
      removeTypename,
    );

    const result = await mutate({
      variables: {
        input: {
          id: simulationCaseId,
          simulationCaseInput: {
            title: input.title,
            patientInformation: markdownInput(input.patientInfo),
            status: contentStatus,
            module: moduleSelected.value,
            difficulty: difficultySelected?.value,
            clinicalCategory: categorySelected?.value,
            assessments: cleanedAssessments,
            diagnosis: diagnosis?.map(removeTypename) ?? [
              { possibleOutcome: "", isCorrectOutcome: true },
            ],
            treatment: treatment?.map(removeTypename) ?? [
              { possibleOutcome: "", isCorrectOutcome: true },
            ],
            referral: referral?.map(removeTypename) ?? [
              { possibleOutcome: "", isCorrectOutcome: true },
            ],
            instantReferral: {
              referralContent: input.instantReferral,
              referralValue: instantRefValue,
            },
            nextCase: {
              title: nextCase,
              id: extractIdFromNextCase(nextCase),
            },
          },
        },
      },
      refetchQueries: [
        {
          query: QUERY,
          variables: { id: simulationCaseId },
        },
      ],
      awaitRefetchQueries: true,
    });

    if (result.data) {
      exitEditPage();
    }
  };

  const exitEditPage = () => {
    navigate(urls.simulationAdminOverview);
  };

  return (
    <AdministrationLayout title="Simuleringsbasert læring">
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <div className={styles.spacing}>
            <TextField
              title="Tittel"
              fieldProps={methods.register("title", {
                required: "Du må gi casen din en tittel",
              })}
              defaultValue={title}
            />

            <div>
              <Label
                title="Modul, vanskelighetsgrad og kategori"
                htmlFor={""}
              />
              <ReactSelect
                name="module"
                className={styles.reactselect}
                options={modules}
                placeholder="Du må velge en modul casen skal tilhøre (klikk her for å spesifisere)"
                isMulti={false}
                styles={{
                  option: (provided) => ({
                    ...provided,
                    color: "black",
                  }),
                }}
                onChange={setModuleSelected}
                value={moduleSelected}
              />

              <ReactSelect
                name="difficulty"
                className={styles.reactselect}
                options={difficulties}
                placeholder="Du må velge en vanskelighetsgrad for casen (klikk her for å spesifisere)"
                isMulti={false}
                styles={{
                  option: (provided) => ({
                    ...provided,
                    color: "black",
                  }),
                }}
                onChange={setDifficultySelected}
                value={difficultySelected}
              />

              <ReactSelect
                name="clinicalCategory"
                className={styles.reactselect}
                options={clinicalCategories}
                placeholder="Du må velge kategori for casen (klikk her for å spesifisere)"
                isMulti={false}
                styles={{
                  option: (provided) => ({
                    ...provided,
                    color: "black",
                  }),
                }}
                onChange={setCategorySelected}
                value={categorySelected}
              />
            </div>

            <div className={styles.statusText}>
              <Select
                name="status"
                title="Status"
                options={contentStatusOptions()}
                value={contentStatus}
                onChange={(newValue: ContentStatus) => {
                  setContentStatus(newValue);
                }}
                defaultValue={contentStatus}
              />
            </div>

            <Field
              title={"Neste kasuistikk i sykdomsforløpet"}
              fieldProps={methods.register("nextCase", {})}
              value={nextCase}
              placeholder="Ingen progresjon har blitt lagt til enda"
            />

            {nextCase === "" ? (
              <CreateContentFooterBox
                buttonText={"Endre det neste steget i sykdomsforløpet"}
                content={AddNextCase}
                onUpdated={() => {}}
                state={setNextCase}
              />
            ) : (
              <Button
                type="button"
                onClick={() => {
                  setNextCase("");
                }}
              >
                Fjern det neste steget i sykdomsforløpet
              </Button>
            )}

            <hr className={styles.separator} />

            <div className={styles.outcomeOption}>
              <div className={styles.textField}>
                <TextField
                  title={
                    "Bør pasienten sendes videre med en gang før diagnose settes?"
                  }
                  fieldProps={methods.register("instantReferral", {
                    required: "Du må gi alternativet en tittel",
                  })}
                  defaultValue={
                    instantReferral?.referralContent ?? "Ingen henvisning"
                  }
                />
              </div>
              <input
                type="checkbox"
                checked={instantRefValue}
                onChange={() => {
                  setInstantRefValue(!instantRefValue);
                }}
                className={styles.checkbox}
              />
            </div>

            <MarkdownField<FormValues>
              name="patientInfo"
              title="Beskrivelse av case"
              defaultValue={patientInformation?.content}
            />
          </div>

          <hr className={styles.separator} />

          <div style={{ display: "flex", alignItems: "center" }}>
            {assessments.length > 0 && (
              <>
                <div
                  onMouseDown={() => {
                    changeAssessment(currentAssessmentIndex - 1);
                  }}
                  role="button"
                  tabIndex={0}
                  style={{
                    cursor: currentAssessmentIndex > 0 ? "pointer" : "default",
                    opacity: currentAssessmentIndex > 0 ? 1 : 0.5,
                  }}
                >
                  <img
                    style={{ width: "2vw" }}
                    src={leftArrowLogo}
                    alt="left-arrow"
                  />
                </div>

                <div style={{ margin: "0 10px", flexGrow: 1 }}>
                  <TextField
                    title={`Tittel for undersøkelse ${currentAssessmentIndex + 1}`}
                    fieldProps={methods.register(
                      `assessments.${currentAssessmentIndex}.testName`,
                      {
                        required: "Du må gi din undersøkelse en tittel",
                      },
                    )}
                    defaultValue={
                      assessments[currentAssessmentIndex]
                        ? assessments[currentAssessmentIndex].testName
                        : ""
                    }
                  />
                  <MarkdownField<FormValues>
                    name={`assessments.${currentAssessmentIndex}.testResult.content`}
                    title={`Innhold for undersøkelse ${currentAssessmentIndex + 1}`}
                    defaultValue={
                      assessments[currentAssessmentIndex]
                        ? assessments[currentAssessmentIndex].testResult.content
                        : ""
                    }
                  />
                  <Button
                    type="button"
                    kind="secondary"
                    className={styles.removeButton}
                    onClick={() => {
                      removeAssessment(currentAssessmentIndex);
                    }}
                  >
                    Fjern undersøkelse
                  </Button>
                </div>
                <div
                  onMouseDown={() => {
                    changeAssessment(currentAssessmentIndex + 1);
                  }}
                  role="button"
                  tabIndex={0}
                  style={{
                    cursor:
                      currentAssessmentIndex < assessments.length - 1
                        ? "pointer"
                        : "default",
                    opacity:
                      currentAssessmentIndex < assessments.length - 1 ? 1 : 0.5,
                  }}
                >
                  <img
                    style={{ width: "2vw" }}
                    src={rightArrowLogo}
                    alt="right-arrow"
                  />
                </div>
              </>
            )}
          </div>

          <Button
            type="button"
            onClick={addAssessment}
            className={styles.addButton}
          >
            Legg til undersøkelse
          </Button>

          <MutationLoadingOrError loading={loading} error={error} />

          <CancelOrSubmit
            onCancel={exitEditPage}
            isLoading={loading}
            cancelText="Gå tilbake"
            submitText="Lagre endringer"
          />
        </form>
      </FormProvider>
      <div id={"bottomRef"} ref={bottomRef} />
    </AdministrationLayout>
  );
};
